import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Layout, Menu, Spin } from 'antd';
import { MenuProps } from 'antd';
import { useQuery } from 'react-query';
import { useLocalStorageState } from 'ahooks';
import { useDispatch } from 'react-redux';
import { SidebarLogo } from './SidebarLogo';
import { getMenuItems, MenuConfiguration } from './menu-config';
import { SidebarActionCards } from './sidebar-action-cards/index';
import { SetupProgressModal } from './SetupProgressModal';

import { useAppSelector } from '../../../hooks/use-app-selector';

import { t } from '../../../utils/i18n';
import { changeLocation } from '../../../actions';
import { useGlobals } from '../../../globals-context';
import createQueryConfig from '../../../queries';
import {
  isResellerAccount,
  isWhitelabelAccount,
  isTrialPhasePromoActive,
  isUpsellShorePOSActive,
  isShorePOSExternalLinkActive,
} from '../../../shared/utils';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { getAppSettings } from '../../../selectors';
import { BOOKING_BASE_URL, POS_MANAGER_BASE_URL } from '../../../constants';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';
import { buildScreenRouteMapping } from '../../../constants/screen-routes';

const { Sider } = Layout;

const StyledSider = styled(Sider)`
  background: white;
  border-right: none;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 100vh;
  border-inline-end: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }

  .ant-layout-sider-trigger {
    display: none;
  }
`;

const StyledMenu = styled(Menu)`
  padding: 5px;
  background: transparent;
  border-inline-end: unset !important;

  .anticon {
    color: rgba(0, 0, 0, 0.45) !important;
  }

  .ant-v5-menu-item {
    height: 48px;
  }

  .menu-item-locked {
    color: ${({ theme }) => theme.antd.colorTextDisabled};

    .anticon {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }

  .ant-v5-menu-submenu {
    border-radius: unset;
    border-top: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};

    .ant-v5-menu-submenu-title {
      text-transform: uppercase;
    }
  }
`;

const SIDE_MENU_CLICK_EVENT = 'side_menu_item_clicked';

interface SideMenuProps {
  screenName: string;
  sideMenuCollapsed: boolean;
}

export function SideMenu({ sideMenuCollapsed, screenName }: SideMenuProps) {
  const dispatch = useDispatch();
  const [openSetupProgressModal, setOpenSetupProgressModal] = useState(false);
  const { queryClient, apiClient, trackMethod, eventBus } = useGlobals() as any;
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { merchantSettings } = useMerchantSettings();
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const sessionPermissions = useAppSelector(
    (state) => state.sessionPermissions
  );
  const { subscriptionPartner } =
    useAppSelector((state) => getAppSettings(state)) || {};

  const accountLocale = currentAccount?.locale || 'en';
  const { company, trialDaysLeft } = merchantSettings || {};

  const merchantQuery = currentMerchant.id
    ? queryConfig.merchantV2(currentMerchant.id)
    : null;

  const { data: merchantData } = useQuery({
    ...merchantQuery,
    enabled: Boolean(merchantQuery),
  }) as any;

  const locationsQuery = queryConfig.locations();
  const { data: locationsData } = useQuery({
    ...locationsQuery,
  }) as any;

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [openKeys, setOpenKeys] = useLocalStorageState<string[]>(
    'as:menuOpenKeys',
    {
      defaultValue: [],
      listenStorageChange: false,
    }
  );

  const isInitialized = useRef(false);
  const prevScreenName = useRef('');
  const prevSideMenuCollapsed = useRef(sideMenuCollapsed);

  const menuConfig: MenuConfiguration = useMemo(() => {
    const isTrialUser = isTrialPhasePromoActive({
      companyName: company,
      trialDaysLeft,
      roles: currentAccount?.roles || [],
    });

    const hasMultipleLocations = (currentAccount?.roles?.length ?? 0) > 1;
    const bookingPageUrl = `${BOOKING_BASE_URL}/widget/${merchantData?.slug}?locale=${accountLocale}`;

    const isPOSUpsellActive = isUpsellShorePOSActive({
      locale: accountLocale,
      companyName: company,
      packageCodes: currentAccount?.contractDetails?.packages ?? [],
    });

    const isPOSExternalLinkActive = isShorePOSExternalLinkActive({
      companyName: company,
      packageCodes: currentAccount?.contractDetails?.packages ?? [],
    });

    const isWhitelabel = isWhitelabelAccount(currentMerchant.whiteLabel);
    const isReseller = isResellerAccount(subscriptionPartner);

    return {
      sessionPermissions,
      isTrialUser,
      isWhitelabel,
      isReseller,
      isPOSUpsellActive,
      isPOSExternalLinkActive,
      bookingPageUrl,
      posManagerUrl: POS_MANAGER_BASE_URL,
      merchantSlug: merchantData?.slug,
      currentAccountId: currentAccount?.id,
      hasMultipleLocations,
      translate: t,
    };
  }, [
    company,
    trialDaysLeft,
    currentAccount,
    locationsData,
    merchantData,
    accountLocale,
    currentMerchant,
    subscriptionPartner,
    sessionPermissions,
  ]);

  const menuItems = useMemo(
    () => getMenuItems(menuConfig),
    [menuConfig]
  ) as MenuProps['items'];

  const isDataLoading = useMemo(() => {
    return (
      Object.values(sessionPermissions).every((value) => value == null) ||
      !currentAccount?.id
    );
  }, [sessionPermissions, currentAccount]);

  const screenRouteMapping = useMemo(
    () =>
      buildScreenRouteMapping({
        bookingPageUrl: menuConfig.bookingPageUrl,
        posManagerUrl: POS_MANAGER_BASE_URL,
      }),
    [menuConfig.bookingPageUrl]
  );

  useEffect(() => {
    const mapping = screenRouteMapping[screenName];

    if (!mapping) {
      if (screenName.includes('#/')) {
        if (prevScreenName.current !== screenName || !isInitialized.current) {
          prevScreenName.current = screenName;
          if (selectedKey !== screenName) {
            setSelectedKey(screenName);
          }
          isInitialized.current = true;
        }
      }
      return;
    }

    if (prevScreenName.current === screenName && isInitialized.current) {
      return;
    }
    prevScreenName.current = screenName;
    const newSelectedKey = mapping.key;

    if (selectedKey !== newSelectedKey) {
      setSelectedKey(newSelectedKey);
    }

    // Update openKeys based on parent, but only if not collapsed
    // Don't re-trigger if only collapsed changed
    if (!sideMenuCollapsed && mapping.parent) {
      const newOpenKeys = [mapping.parent];
      if (JSON.stringify(openKeys) !== JSON.stringify(newOpenKeys)) {
        setOpenKeys(newOpenKeys);
      }
    } else if (sideMenuCollapsed && openKeys && openKeys.length > 0) {
      // Clear openKeys when collapsed
      setOpenKeys([]);
    }

    isInitialized.current = true;
    prevSideMenuCollapsed.current = sideMenuCollapsed;
  }, [screenRouteMapping, screenName, sideMenuCollapsed]);

  const handleOpenChange = (keys: string[]) => {
    if (!keys || keys.length === 0) {
      setOpenKeys([]);
      return;
    }
    const latest = keys[keys.length - 1];
    setOpenKeys(latest ? [latest] : []);
  };

  // TODO: Make this onClick handler part better in next iteration
  const handleOnMenuClick: MenuProps['onClick'] = (e) => {
    const menuItem = menuItems?.find((item: any) => item?.key === e.key);
    if (menuItem && (menuItem as any).disabled) {
      return;
    }

    if (e.key.includes('#/')) {
      window.location.hash = e.key;

      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key.replace(/#\/|\//g, ''),
      });

      return;
    }

    if (e.key.startsWith('http://') || e.key.startsWith('https://')) {
      window.open(e.key, '_blank');
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key.replace(/#\/|\//g, ''),
      });
      return;
    }

    if (e.key.startsWith('/')) {
      const route = e.key;
      setSelectedKey(e.key);
      dispatch(changeLocation(route));
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key.replace(/#\/|\//g, ''),
      });
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
    }
  };

  return (
    <StyledSider
      collapsible
      collapsed={sideMenuCollapsed}
      trigger={null}
      width={260}
      collapsedWidth={80}
    >
      <SidebarLogo
        isLoading={isDataLoading}
        sideMenuCollapsed={sideMenuCollapsed}
      />
      {
        // TODO: Make this loading state with
        // Skeleton Component for Menu
      }
      {isDataLoading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 120px)',
            width: '100%',
            gap: 12,
          }}
        >
          <Spin size="large" />
          <div style={{ color: '#8c8c8c', fontSize: 14 }}>Loading...</div>
        </div>
      ) : (
        <>
          <StyledMenu
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={handleOnMenuClick}
          />
          <SidebarActionCards
            sideMenuCollapsed={sideMenuCollapsed}
            onSetupProgressCardClick={() => setOpenSetupProgressModal(true)}
          />
          <SetupProgressModal
            open={openSetupProgressModal}
            onCloseClick={() => setOpenSetupProgressModal(false)}
          />
        </>
      )}
    </StyledSider>
  );
}
